// frontend/src/components/DocumentsList.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Button, Modal, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import api from '../api';

const CATEGORIES = ['plan', 'photo', 'contrat', 'facture', 'devis', 'autre'];

const CATEGORIE_BADGE = {
  plan:     { bg: 'primary',   label: '📐 Plan' },
  photo:    { bg: 'info',      label: '📷 Photo' },
  contrat:  { bg: 'success',   label: '📜 Contrat' },
  facture:  { bg: 'warning',   label: '💰 Facture' },
  devis:    { bg: 'secondary', label: '📋 Devis' },
  autre:    { bg: 'dark',      label: '📎 Autre' },
};

function DocumentsList({ onDocumentChange }) {
  const [documents, setDocuments]       = useState([]);
  const [chantiers, setChantiers]       = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [showModal, setShowModal]       = useState(false);
  const [uploading, setUploading]       = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(null);
  const [filtreChantier, setFiltreChantier]   = useState('');
  const [filtreCategorie, setFiltreCategorie] = useState('');
  const [form, setForm] = useState({
    chantier_id: '', categorie: 'autre', nom: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      let params = '';
      if (filtreChantier) params += `?chantier_id=${filtreChantier}`;
      if (filtreCategorie) params += `${params ? '&' : '?'}categorie=${filtreCategorie}`;
      const response = await api.get(`/documents${params}`);
      setDocuments(response.data);
      if (onDocumentChange) onDocumentChange(response.data.length);
    } catch (err) {
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [filtreChantier, filtreCategorie]);

  const fetchChantiers = useCallback(async () => {
    try {
      const response = await api.get('/chantiers');
      setChantiers(response.data);
    } catch (err) {
      console.error('Erreur chargement chantiers:', err);
    }
  }, []);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);
  useEffect(() => { fetchChantiers(); }, [fetchChantiers]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      if (!form.nom) setForm({ ...form, nom: file.name });
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) return;

    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('nom', form.nom || selectedFile.name);
    formData.append('categorie', form.categorie);
    if (form.chantier_id) formData.append('chantier_id', form.chantier_id);

    try {
      await api.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setShowModal(false);
      setSelectedFile(null);
      setForm({ chantier_id: '', categorie: 'autre', nom: '' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      await fetchDocuments();
    } catch (err) {
      setError(`Erreur upload: ${err.response?.data?.error || err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    setDownloadLoading(doc.id);
    try {
      const response = await api.get(`/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.nom_fichier);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Erreur téléchargement: ${err.message}`);
    } finally {
      setDownloadLoading(null);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Supprimer "${doc.nom}" ?`)) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      await fetchDocuments();
    } catch (err) {
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
        <p className="mt-2">Chargement des documents...</p>
      </div>
    );
  }

  return (
    <>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>
      )}

      {/* Barre d'outils */}
      <div className="d-flex justify-content-between align-items-center mb-3 mt-4">
        <div className="d-flex align-items-center gap-2">
          <h4 className="mb-0">Documents ({documents.length})</h4>
          <Form.Select size="sm" style={{ width: '180px' }}
            value={filtreChantier} onChange={(e) => setFiltreChantier(e.target.value)}>
            <option value="">Tous les chantiers</option>
            {chantiers.map(c => (
              <option key={c.id} value={c.id}>{c.nom}</option>
            ))}
          </Form.Select>
          <Form.Select size="sm" style={{ width: '140px' }}
            value={filtreCategorie} onChange={(e) => setFiltreCategorie(e.target.value)}>
            <option value="">Toutes catégories</option>
            {CATEGORIES.map(c => (
              <option key={c} value={c}>{CATEGORIE_BADGE[c].label}</option>
            ))}
          </Form.Select>
        </div>
        <Button variant="primary" onClick={() => setShowModal(true)}>
          ⬆️ Uploader un document
        </Button>
      </div>

      {documents.length === 0 ? (
        <Alert variant="info">
          Aucun document pour le moment.{' '}
          <Button variant="link" className="p-0" onClick={() => setShowModal(true)}>
            Uploader le premier document
          </Button>
        </Alert>
      ) : (
        <Table striped bordered hover responsive>
          <thead className="table-dark">
            <tr>
              <th>Fichier</th>
              <th>Chantier</th>
              <th>Catégorie</th>
              <th>Taille</th>
              <th>Uploadé par</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {documents.map((doc) => (
              <tr key={doc.id}>
                <td>
                  <span style={{ fontSize: '1.2em', marginRight: '8px' }}>{doc.icone}</span>
                  <strong>{doc.nom}</strong>
                  <div><small className="text-muted">{doc.nom_fichier}</small></div>
                </td>
                <td>{doc.chantier_nom || <span className="text-muted">—</span>}</td>
                <td>
                  <Badge bg={CATEGORIE_BADGE[doc.categorie]?.bg || 'dark'}>
                    {CATEGORIE_BADGE[doc.categorie]?.label || doc.categorie}
                  </Badge>
                </td>
                <td>{doc.taille}</td>
                <td>{doc.uploaded_by || '—'}</td>
                <td>{doc.created_at ? doc.created_at.substring(0, 10) : '—'}</td>
                <td>
                  <div className="d-flex gap-1">
                    <Button size="sm" variant="outline-primary"
                      onClick={() => handleDownload(doc)}
                      disabled={downloadLoading === doc.id}
                      title="Télécharger">
                      {downloadLoading === doc.id ? '⏳' : '⬇️'}
                    </Button>
                    <Button size="sm" variant="outline-danger"
                      onClick={() => handleDelete(doc)}
                      title="Supprimer">🗑️</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Modal upload */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>⬆️ Uploader un document</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpload}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}

            <Form.Group className="mb-3">
              <Form.Label>Fichier *</Form.Label>
              <Form.Control type="file" ref={fileInputRef}
                onChange={handleFileChange} required
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.zip,.txt,.csv" />
              <Form.Text className="text-muted">
                PDF, Word, Excel, images, ZIP — max 20 Mo
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Nom du document</Form.Label>
              <Form.Control type="text" value={form.nom}
                onChange={(e) => setForm({ ...form, nom: e.target.value })}
                placeholder="Laisser vide pour utiliser le nom du fichier" />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Chantier associé</Form.Label>
              <Form.Select value={form.chantier_id}
                onChange={(e) => setForm({ ...form, chantier_id: e.target.value })}>
                <option value="">— Aucun chantier —</option>
                {chantiers.map(c => (
                  <option key={c.id} value={c.id}>{c.nom}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Catégorie</Form.Label>
              <Form.Select value={form.categorie}
                onChange={(e) => setForm({ ...form, categorie: e.target.value })}>
                {CATEGORIES.map(c => (
                  <option key={c} value={c}>{CATEGORIE_BADGE[c].label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Annuler</Button>
            <Button variant="primary" type="submit" disabled={uploading || !selectedFile}>
              {uploading ? '⏳ Upload en cours...' : '⬆️ Uploader'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}

export default DocumentsList;
